/**
 * Shared left-nav shell for Organization Settings and the full-page role
 * create/edit routes (/admin/roles/new, /admin/roles/:roleId).
 *
 * Extracted from OrganizationSettingsPage so that navigating from the
 * "Roles & Permissions" tab to the Edit/Add Role page keeps the exact same
 * left navigation instead of swapping to the main app sidebar — those role
 * routes render under RoleRoute's `noSidebar` layout and wrap their content
 * in this component directly (see App.tsx).
 *
 * Structurally mirrors the main app's Sidebar.tsx + Header.tsx split
 * (2026-09-01): this file's <aside> owns the "AI Recruit" logo in its own
 * header row (same as Sidebar.tsx), and SettingsTopBar below owns only the
 * collapse toggle + user menu in a separate bar (same as Header.tsx) —
 * previously both lived combined in NoSidebarLayout.tsx's BrandHeader.
 */

import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'
import {
  UserCircleIcon,
  TagIcon,
  CurrencyDollarIcon,
  LockClosedIcon,
  ClipboardDocumentListIcon,
  AdjustmentsHorizontalIcon,
  LifebuoyIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline'
import { BuildingOffice2Icon } from '@heroicons/react/24/solid'
import { Users2, Bot, User, ChevronDown, Building2, Key, LogOut, Menu } from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { useAuth } from '@/hooks/useAuth'
import { useClickOutside } from '@/hooks/useClickOutside'
import { ORG_SETTINGS_CAPABILITIES } from '@/config/permissions'
import ChangePasswordModal from '@/components/common/ChangePasswordModal'
import Tooltip from '@/components/common/Tooltip'
import { toggleSidebar } from '@/stores/ui/uiSlice'
import type { RootState } from '@/stores'

export type SectionKey = 'teams' | 'users' | 'sources' | 'cost-dashboard' | 'roles' | 'questionTypes' | 'complexityLevels' | 'assessmentSupport' | 'resumeTemplates' | 'reports'

interface NavItem {
  key: SectionKey
  label: string
  icon: React.FC<{ className?: string }>
  description: string
}

export function useOrgSettingsNavItems() {
  const { can, role, isBypassAll } = usePermission()
  // roles.manage retired 2026-08-31 -> six ordinary keys. viewList is the
  // minimum to see the tab at all; viewAuditLog alone also earns a spot
  // (an auditor-only role with no roles.viewList should still reach the
  // History views, same "OR across the tier's own keys" pattern as Teams/
  // Sources/Users above).
  const canSeeRolesSection = can('roles.viewList') || can('roles.viewAuditLog')
  // questionTypes.view/manage aren't in the RBAC catalog yet (new feature —
  // see ProctoredInterviewTab), so this falls back to the same admin-tier
  // role check the backend uses (gated()'s legacy_check) rather than a
  // capability that would resolve to false for every role until seeded.
  const canSeeQuestionTypes = isBypassAll || (!!role && ['super_admin', 'admin', 'manager'].includes(role))

  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = []
    if (
      can('teams.viewOwn') ||
      can('teams.create') ||
      can('teams.rename') ||
      can('teams.reassignManager') ||
      can('teams.delete')
    ) {
      items.push({
        key: 'teams',
        label: 'Teams',
        icon: ({ className }) => <Users2 className={className} />,
        description: 'Manage teams and members',
      })
    }
    if (
      can('sources.createJobSource') ||
      can('sources.editJobSource') ||
      can('sources.activateDeactivateJobSource') ||
      can('sources.createResumeSource') ||
      can('sources.editResumeSource') ||
      can('sources.activateDeactivateResumeSource') ||
      can('sources.viewJobSources') ||
      can('sources.viewResumeSources')
    ) {
      items.push({
        key: 'sources',
        label: 'Manage Sources',
        icon: ({ className }) => <TagIcon className={className} />,
        description: 'Manage job and resume sources',
      })
    }
    if (
      can('users.create') ||
      can('users.edit') ||
      can('users.delete') ||
      can('users.resetPasswordDefault') ||
      can('users.setCustomPassword') ||
      can('users.view')
    ) {
      items.push({
        key: 'users',
        label: 'Users',
        icon: ({ className }) => <UserCircleIcon className={className} />,
        description: 'Manage organization users',
      })
    }
    if (can('analytics.viewCostDashboard')) {
      items.push({
        key: 'cost-dashboard',
        label: 'Cost Dashboard',
        icon: ({ className }) => <CurrencyDollarIcon className={className} />,
        description: 'AI agent cost and usage analytics',
      })
    }
    if (canSeeRolesSection) {
      items.push({
        key: 'roles',
        label: 'Roles & Permissions',
        icon: ({ className }) => <LockClosedIcon className={className} />,
        description: 'Create custom roles and manage capabilities',
      })
    }
    if (canSeeQuestionTypes) {
      items.push({
        key: 'questionTypes',
        label: 'Question Types',
        icon: ({ className }) => <ClipboardDocumentListIcon className={className} />,
        description: 'Manage proctored interview question types',
      })
      // Gated on the same roles as Question Types: both are proctored-assessment
      // configuration, and proctoredInterviews.* is not in the RBAC catalog
      // yet either (see canSeeQuestionTypes above for the same reasoning).
      items.push({
        key: 'assessmentSupport',
        label: 'Assessment Support',
        icon: ({ className }) => <LifebuoyIcon className={className} />,
        description: 'Contact details shown when an assessment is interrupted',
      })
    }
    if (can('complexityLevels.view')) {
      items.push({
        key: 'complexityLevels',
        label: 'Complexity Levels',
        icon: ({ className }) => <AdjustmentsHorizontalIcon className={className} />,
        description: 'Complexity levels for proctored interview questions',
      })
    }
    if (can('reports.viewDownload')) {
      items.push({
        key: 'reports',
        label: 'Reports',
        icon: ({ className }) => <DocumentChartBarIcon className={className} />,
        description: 'Download usage and hiring reports',
      })
    }
    if (can('resumeTemplates.view') || can('resumeTemplates.manage')) {
      items.push({
        key: 'resumeTemplates',
        label: 'Resume Templates',
        icon: ({ className }) => <DocumentTextIcon className={className} />,
        description: 'AI-generated, per-client resume formatting templates',
      })
    }
    return items
  }, [can, canSeeRolesSection, canSeeQuestionTypes])

  return { navItems, canSeeRolesSection }
}

// ─── Top bar — collapse toggle + user menu only, same split as Header.tsx ───
// Moved here from NoSidebarLayout.tsx's old combined BrandHeader (2026-09-01)
// so this bar's only job — same as the main app's Header.tsx — is the
// sidebar toggle and the user menu, with the brand logo living in the
// sidebar's own header below instead.

const SettingsTopBar: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const { logout } = useAuth()
  const { can } = usePermission()
  // §25 — mirrors /org-settings' own route guard exactly (the OR of its 6
  // real sub-capabilities), not a separate `orgSettings.access` capability
  // (removed — it had no purpose independent of these six).
  const canAccessOrgSettings = ORG_SETTINGS_CAPABILITIES.some((c) => can(c))

  const [menuOpen, setMenuOpen] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const menuRef = useClickOutside(() => setMenuOpen(false))

  return (
    <>
      {/* Markup copied verbatim from Header.tsx (bg-white shadow-sm
          border-b border-gray-200 header, h-16 px-4 sm:px-6 lg:px-8 inner
          row, left/right flex items-center space-x-4 clusters) so padding,
          alignment, and spacing match exactly — only the mobile/desktop
          duplicate toggle is collapsed to one button, since this app is
          desktop-only. */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Left side */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => dispatch(toggleSidebar())}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900 leading-tight">{user?.fullName}</p>
                  <p className="text-xs text-gray-500 capitalize leading-tight">{user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <div className="py-2">
                    {canAccessOrgSettings && (
                      <button
                        onClick={() => { navigate('/org-settings'); setMenuOpen(false) }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Building2 className="w-4 h-4 mr-3 flex-shrink-0" />
                        Organization Settings
                      </button>
                    )}
                    <button
                      onClick={() => { setChangePasswordOpen(true); setMenuOpen(false) }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Key className="w-4 h-4 mr-3 flex-shrink-0" />
                      Reset Password
                    </button>
                    <div className="border-t border-gray-200 my-1" />
                    <button
                      onClick={async () => { await logout(); setMenuOpen(false) }}
                      className="flex items-center w-full px-4 py-2 text-sm text-error-600 hover:bg-error-50"
                    >
                      <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  )
}

interface OrgSettingsLayoutProps {
  activeSection: SectionKey
  children: React.ReactNode
  // Used by OrganizationSettingsPage to switch tabs in place (replacing the
  // ?tab= query param) instead of the default push navigation used when
  // this shell wraps a separate route (e.g. /admin/roles/:roleId).
  onSelect?: (key: SectionKey) => void
}

export const OrgSettingsLayout: React.FC<OrgSettingsLayoutProps> = ({
  activeSection,
  children,
  onSelect,
}) => {
  const navigate = useNavigate()
  const { navItems } = useOrgSettingsNavItems()
  // Same Redux state the main app Sidebar's collapse toggle reads/writes
  // (stores/ui/uiSlice.ts) — toggled from SettingsTopBar above. The two
  // sidebars are never mounted at the same time (this one only renders
  // under RoleRoute's noSidebar layout), so sharing the one flag is the
  // same toggle, not a lookalike copy, and needs no new state.
  const { sidebarOpen } = useSelector((state: RootState) => state.ui)

  // Defends against `activeSection` no longer being in `navItems` (e.g. the
  // capability backing it was revoked) the same way OrganizationSettingsPage
  // always has — fall back to whichever item actually rendered instead of
  // leaving the content panel blank.
  const effectiveActiveSection = navItems.some((i) => i.key === activeSection)
    ? activeSection
    : navItems[0]?.key

  const handleSelect = (key: SectionKey) => {
    if (onSelect) {
      onSelect(key)
    } else {
      navigate(`/org-settings?tab=${key}`)
    }
  }

  return (
    <div className="flex h-full min-h-0 bg-gray-50">
      {/* Left navigation panel */}
      <aside className={clsx(
        'flex-shrink-0 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out',
        sidebarOpen ? 'w-64' : 'w-16'
      )}>
        {/* AI Recruit logo — same content, position (own header row, h-16,
            border-b), and collapse behavior as the main app's own logo in
            Sidebar.tsx. Moved here (2026-09-01) from the old combined
            NoSidebarLayout header so the logo lives inside this sidebar's
            own header, not a shared bar. */}
        <Link
          to="/dashboard"
          className={clsx(
            'h-16 flex items-center gap-3 px-5 border-b border-gray-100 flex-shrink-0 group',
            !sidebarOpen && 'justify-center px-0'
          )}
        >
          <div className="flex items-center justify-center w-8 h-8 bg-gradient-primary rounded-lg flex-shrink-0">
            <Bot className="w-5 h-5 text-white" />
          </div>
          {/* Not rendered at all when collapsed (rather than kept at
              opacity-0/w-0) — a zero-width sibling still reserves its gap,
              which pushed the icon off-center; dropping the element
              entirely leaves the icon as the sole flex child, centered
              exactly. */}
          {sidebarOpen && (
            <span className="text-xl font-bold text-gradient whitespace-nowrap">
              AI Recruit
            </span>
          )}
        </Link>

        {/* Organization Settings — pre-existing section identifier, kept as
            its own row below the logo (only the logo's structural placement
            was asked to move, not this content). */}
        <div className={clsx('px-5 py-5 border-b border-gray-100', !sidebarOpen && 'px-0')}>
          <div className={clsx('flex items-center gap-2.5', !sidebarOpen && 'justify-center')}>
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <BuildingOffice2Icon className="w-5 h-5 text-primary-600" />
            </div>
            {sidebarOpen && (
              <p className="text-sm font-bold text-gray-900 leading-tight whitespace-nowrap">Organization Settings</p>
            )}
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          <p className={clsx(
            'px-2 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider transition-opacity duration-300',
            !sidebarOpen && 'opacity-0'
          )}>
            Team Management
          </p>
          {navItems.map((item) => {
            const isActive = effectiveActiveSection === item.key
            const navButton = (
              <button
                key={item.key}
                onClick={() => handleSelect(item.key)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors duration-150 ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                } ${!sidebarOpen ? 'justify-center' : ''}`}
              >
                <item.icon
                  className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-gray-400'}`}
                />
                {sidebarOpen && (
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.label}</p>
                    <p className={`text-xs truncate ${isActive ? 'text-primary-500' : 'text-gray-400'}`}>
                      {item.description}
                    </p>
                  </div>
                )}
              </button>
            )
            return !sidebarOpen ? (
              <Tooltip key={item.key} content={item.label} position="right" delay={0}>
                {navButton}
              </Tooltip>
            ) : (
              navButton
            )
          })}
        </nav>
      </aside>

      {/* Content column — top bar (toggle + user menu, same role as
          Header.tsx) above the scrollable content, same split as
          Layout.tsx's <Header/> + <main/> next to <Sidebar/>. */}
      <div className="flex-1 flex flex-col min-h-0">
        <SettingsTopBar />
        <main className="flex-1 overflow-y-auto">
          {/* py-6 matches Layout.tsx's own content wrapper exactly (was
              py-8 — extra top gap the main app's equivalent doesn't have).
              max-w widens when collapsed (was a fixed max-w-5xl always) —
              otherwise the sidebar's reclaimed ~192px just became a bigger
              empty right margin instead of actually being used by the
              content, same complaint as the tick-grid's column count. */}
          <div className={clsx(
            'mx-auto px-6 py-6 transition-all duration-300',
            sidebarOpen ? 'max-w-5xl' : 'max-w-7xl'
          )}>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default OrgSettingsLayout
